/**
 * Created by Admin on 08.05.2018.
 */

import {
  alertError,
  createJsonHeaders,
  logoutOnRequestOrResponseJson,
} from './api'
import { saveAdminUser } from './tabs/authorization/actions/adminUser'
import { BASE_URL } from './configs'
import { nlpRoutes } from 'routes/nlpRoutes'
import { settingsRoutes } from 'routes/settingsRoutes'
import {
  isQcsBotPayload,
  isSettingsRouteAllowedForQcs,
  isQcsOnlySettingsRoute,
  isSettingsRouteHiddenForQcsManager,
} from 'constants/qcs'
import { isProfessionalsNavAllowed } from 'tabs/home/api/usageLimits'
import * as FullStory from '@fullstory/browser'

export const ROLE = {
  ADMIN: 'ROLE_ADMIN',
  CARECO_ADMIN: 'ROLE_CARECO_ADMIN',
  MANAGER: 'ROLE_MANAGER',
}

const getSimpleAdminAllowedNlpRoutes = () => {
  return nlpRoutes.filter(route => route.name !== 'Intents' && route.name !== 'Triggers' && route.path !== 'ai-knowledge')
}

const getSimpleAdminAllowedSettingsRoutes = () => {
  return settingsRoutes.filter(route => route.path !== 'twoFactorAuth' && route.path !== 'aiFeaturesManagement')
}

export const ROLE_OPTIONS = [
  {
    value: ROLE.ADMIN,
    label: 'Admin',
    features: [
      'createBot',
      'shareBot',
      'deleteBot',
      'dashboard',
      'account-settings',
      'nlp-prompt',
      'nlp-website',
      'nlp-file',
      'nlp-text',
      'flows',
      'atomBuilder',
      'nlp',
      'improve-ai-knowledge',
      'professionals',
      'serviceUsers',
      'support',
      'broadcast',
      'settings',
      // 'widget',
      // 'copilot',
      // 'analytics',
    ],
    nlpRoutes: nlpRoutes,
    settingsRoutes: settingsRoutes,
  },
  {
    value: ROLE.CARECO_ADMIN,
    label: 'Careco Admin',
    features: [
      'dashboard',
      'nlp',
      'nlp-file',
      'nlp-text',
      'professionals',
      'serviceUsers',
      'improve-ai-knowledge',
      'support',
      'broadcast',
      'settings',
    ],
    nlpRoutes: nlpRoutes,
    settingsRoutes: getSimpleAdminAllowedSettingsRoutes(),
  },
  {
    value: ROLE.MANAGER,
    label: 'Care Manager',
    features: [
      'dashboard',
      'nlp',
      'professionals',
      'serviceUsers',
      'support',
      'broadcast',
    ],
    nlpRoutes: getSimpleAdminAllowedNlpRoutes(),
    settingsRoutes: getSimpleAdminAllowedSettingsRoutes(),
  }
]

export const access = {
  userRole: null,
  botAccesses: [],

  loadRole() {
    fetch(BASE_URL + '/getAdminUser', {
      credentials: 'include',
      method: 'GET',
      headers: createJsonHeaders(),
    })
      .then(response => logoutOnRequestOrResponseJson(response))
      .then(json => {
        // access.userRole = json.role;
        access.botAccesses = json.botAccesses
        saveAdminUser(json)
        if (process.env.REACT_APP_FULL_STORY_ORG_ID) {
          FullStory.identify(json.id, { email: json.email })
        }
        return json
      })
      .catch(function (error) {
        alertError('Sorry but something is going wrong please ping support!')
      })
  },
}

export function hasPermissionForBot(botId, featureName) {
  const botAccess = access?.botAccesses?.filter(
    ba => ba.botId === Number(botId),
  )[0]

  return (
    botAccess &&
    ROLE_OPTIONS?.find(
      role => role.value === botAccess.role,
    )?.features?.includes(featureName)
  )
}

export function getUserRole(botId, botAccesses = access?.botAccesses) {
  const normalizedBotId = Number(botId)

  return botAccesses
    ?.filter(ba => Number(ba.botId) === normalizedBotId)
    ?.map(ba => ba.role)
    ?.find(role => role) || ''
}

export function isCarecoAdminForBot(botId, botAccesses = access?.botAccesses) {
  return getUserRole(botId, botAccesses) === ROLE.CARECO_ADMIN
}

// Roles that see the Careco-style dashboard (Careco admin + full admin)
export function canViewCarecoDashboard(botId, botAccesses = access?.botAccesses) {
  const role = getUserRole(botId, botAccesses)
  return role === ROLE.CARECO_ADMIN || role === ROLE.ADMIN
}

export function hasPermissionForFeatureByRole(featureName, userRole) {
  return ROLE_OPTIONS?.find(
    role => role.value === userRole,
  )?.features?.includes(featureName)
}

export function hasPermissionForFeature(featureName, botId) {
  const userRole = getUserRole(botId)

  return hasPermissionForFeatureByRole(featureName, userRole)
}

export function hasPermissionForFeatureInAnyBot(featureName) {
  const userRolesForAllBots = access?.botAccesses.map(ba => ba.botId)

  return userRolesForAllBots?.some(botId => hasPermissionForFeature(featureName, botId))
}

/** True when GET /getAdminUser lists ROLE_ADMIN on at least one organisation. */
export function hasAdminAccessOnAnyBot(botAccesses = access?.botAccesses) {
  return (botAccesses || []).some(ba => ba.role === ROLE.ADMIN)
}

/** Admin on at least one QCS organisation; Admin rights on CareBrain bots don't count. */
export function hasAdminAccessOnAnyQcsBot(bots, botAccesses = access?.botAccesses) {
  return (bots || []).some(
    bot => isQcsBotPayload(bot) && getUserRole(bot.id, botAccesses) === ROLE.ADMIN,
  )
}

export function getAIKnowledgePermissions(botId) {
  const userRole = getUserRole(botId)
  const aiKnowledgeFeatures = ['nlp-prompt', 'nlp-website', 'nlp-file', 'nlp-text']

  const role = ROLE_OPTIONS?.find(role => role.value === userRole)

  return role
    ? aiKnowledgeFeatures.filter(feature => role.features.includes(feature))
    : []
}

export function getAvailableRoles(botId) {
  const userRole = getUserRole(botId)
  // Invite / Share never offers Care Manager (CareBrain and QCS).
  switch (userRole) {
    case ROLE.ADMIN:
      return ROLE_OPTIONS.filter(role => role.value !== ROLE.MANAGER)
    case ROLE.CARECO_ADMIN:
      return ROLE_OPTIONS.filter(
        role => role.value !== ROLE.ADMIN && role.value !== ROLE.MANAGER,
      )
    default:
      return []
  }
}

export function hasPermissionForSettingsRoutes(botId, activeBot) {
  const botAccess = access?.botAccesses?.filter(
    ba => ba.botId === Number(botId),
  )[0]

  const settingsRoutesForRole = botAccess && ROLE_OPTIONS?.find(
    role => role.value === botAccess.role,
  )?.settingsRoutes

  if (!settingsRoutesForRole) {
    return settingsRoutesForRole
  }

  // Role decides what this operator may open; the QCS flag decides what the
  // organisation bought. QCS keeps Platform Admins, Surveys & Supervision,
  // Usage limit tracking, and the hidden Create Supervision editor (no General).
  // Care Manager on QCS then drops Usage limit tracking.
  let allowedRoutes
  if (isQcsBotPayload(activeBot)) {
    allowedRoutes = settingsRoutesForRole.filter(route => isSettingsRouteAllowedForQcs(route.path))
    if (botAccess.role === ROLE.MANAGER) {
      allowedRoutes = allowedRoutes.filter(
        route => !isSettingsRouteHiddenForQcsManager(route.path),
      )
    }
    // Supervision off + never used → hide Surveys & Supervision and Create.
    if (!isProfessionalsNavAllowed(activeBot)) {
      allowedRoutes = allowedRoutes.filter(
        route =>
          route.path !== 'surveysSupervision' &&
          route.path !== 'createSupervision/:templateId?',
      )
    }
  } else {
    allowedRoutes = settingsRoutesForRole.filter(route => !isQcsOnlySettingsRoute(route.path))
  }

  if (activeBot?.apiIntegrationConfig !== 'yes') {
    return allowedRoutes.filter(route => route.path !== 'apiIntegration')
  }

  return allowedRoutes
}
